import { zodResolver } from '@hookform/resolvers/zod';
import { erreurPortable, portableDuContact } from '@imp/shared/controle-saisie';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  type AdresseInput,
  avisGoogle,
  createContact,
  enregistrerAdressePrincipale,
  getAdressePrincipale,
  getContact,
  updateContact,
} from '@/modules/clients/api';
import { ClientSmsCard } from '@/modules/messagerie/ClientSmsCard';

const schema = z
  .object({
    kind: z.enum(['prospect', 'client', 'fournisseur', 'sous_traitant']),
    type_personne: z.enum(['particulier', 'pro']),
    civilite: z.string(),
    nom: z.string(),
    prenom: z.string(),
    raison_sociale: z.string(),
    siret: z.string(),
    code_comptable: z.string(),
    tva_intracom: z.string(),
    naf: z.string(),
    email: z.union([z.string().email('Email invalide'), z.literal('')]),
    sans_email: z.boolean(),
    telephone: z.string(),
    telephone_mobile: z.string(),
    notes: z.string(),
    avis_google: z.enum(['non_demande', 'demande', 'laisse']),
    adresse_ligne1: z.string(),
    adresse_ligne2: z.string(),
    adresse_code_postal: z.string(),
    adresse_ville: z.string(),
  })
  .refine(
    (d) => (d.type_personne === 'pro' ? d.raison_sociale.trim().length > 0 : d.nom.trim().length > 0),
    { message: 'Nom (particulier) ou raison sociale (professionnel) obligatoire', path: ['nom'] },
  )
  // Un client ou un prospect doit pouvoir être joint : un mobile (les relances
  // et rappels partent par SMS), un e-mail — ou la mention qu'il n'en a pas et
  // reçoit tout par courrier — et une adresse complète. Pour un fournisseur ou
  // un sous-traitant, l'adresse reste facultative, mais une adresse commencée
  // doit être finie : une facture avec la rue et sans la ville ne sert à personne.
  .superRefine((d, ctx) => {
    const aJoindre = estAJoindre(d.kind);
    if (aJoindre) {
      // Un portable tapé dans « Téléphone » suffit : beaucoup de fiches n'ont que lui.
      const mobile = erreurPortable(d.telephone_mobile, d.telephone);
      if (mobile !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['telephone_mobile'], message: mobile });
      }
      if (!d.sans_email && d.email.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['email'],
          message: "E-mail obligatoire — ou cochez « Pas d'e-mail : tout par courrier »",
        });
      }
    }
    const commencee =
      d.adresse_ligne1.trim() !== '' ||
      d.adresse_code_postal.trim() !== '' ||
      d.adresse_ville.trim() !== '' ||
      d.adresse_ligne2.trim() !== '';
    if (!commencee && !aJoindre) return;
    const manque: ['adresse_ligne1' | 'adresse_code_postal' | 'adresse_ville', string][] = [
      ['adresse_ligne1', 'Numéro et rue obligatoires'],
      ['adresse_code_postal', 'Code postal obligatoire'],
      ['adresse_ville', 'Ville obligatoire'],
    ];
    for (const [champ, message] of manque) {
      if (d[champ].trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [champ], message });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

/** Les fiches qu'on relance et à qui on envoie devis et factures. */
function estAJoindre(kind: FormValues['kind']): boolean {
  return kind === 'prospect' || kind === 'client';
}

const EMPTY: FormValues = {
  kind: 'prospect',
  type_personne: 'particulier',
  civilite: '',
  nom: '',
  prenom: '',
  raison_sociale: '',
  siret: '',
  code_comptable: '',
  tva_intracom: '',
  naf: '',
  email: '',
  sans_email: false,
  telephone: '',
  telephone_mobile: '',
  notes: '',
  avis_google: 'non_demande',
  adresse_ligne1: '',
  adresse_ligne2: '',
  adresse_code_postal: '',
  adresse_ville: '',
};

export function ClientFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const editQuery = useQuery({
    queryKey: ['contact', id],
    queryFn: () => getContact(id ?? ''),
    enabled: isEdit,
  });

  const adresseQuery = useQuery({
    queryKey: ['contact-adresse', id],
    queryFn: () => getAdressePrincipale(id ?? ''),
    enabled: isEdit,
  });

  useEffect(() => {
    const c = editQuery.data;
    if (!c) return;
    // Sans cette attente, le formulaire se remplirait sans l'adresse, puis un
    // simple « Enregistrer » la supprimerait.
    if (adresseQuery.isLoading) return;
    const a = adresseQuery.data ?? null;
    reset({
      kind: c.kind as FormValues['kind'],
      type_personne: c.type_personne as FormValues['type_personne'],
      civilite: c.civilite ?? '',
      nom: c.nom ?? '',
      prenom: c.prenom ?? '',
      raison_sociale: c.raison_sociale ?? '',
      siret: c.siret ?? '',
      code_comptable: c.code_comptable ?? '',
      tva_intracom: c.tva_intracom ?? '',
      naf: c.naf ?? '',
      email: c.email ?? '',
      sans_email: c.metadata?.sans_email === true,
      telephone: c.telephone ?? '',
      telephone_mobile: c.telephone_mobile ?? '',
      notes: c.notes ?? '',
      avis_google: avisGoogle(c),
      adresse_ligne1: a?.ligne1 ?? '',
      adresse_ligne2: a?.ligne2 ?? '',
      adresse_code_postal: a?.code_postal ?? '',
      adresse_ville: a?.ville ?? '',
    });
  }, [editQuery.data, adresseQuery.data, adresseQuery.isLoading, reset]);

  const mutation = useMutation({
    mutationFn: async (v: FormValues) => {
      const {
        avis_google,
        sans_email,
        adresse_ligne1,
        adresse_ligne2,
        adresse_code_postal,
        adresse_ville,
        ...rest
      } = v;
      // Fusion avec le metadata existant pour ne pas écraser d'autres clés.
      const metadata = { ...(editQuery.data?.metadata ?? {}), avis_google, sans_email };
      const email = sans_email || v.email.trim() === '' ? null : v.email.trim();
      // Le portable va dans « Mobile », là où les SMS le cherchent, même s'il a
      // été tapé dans « Téléphone ».
      const telephone_mobile =
        portableDuContact(v.telephone_mobile, v.telephone) ?? v.telephone_mobile.trim();
      const input = { ...rest, telephone_mobile, email, metadata };
      const contactId = isEdit && id ? id : await createContact(input);
      if (isEdit && id) await updateContact(id, input);

      const ligne2 = adresse_ligne2.trim();
      const adresse: AdresseInput | null =
        adresse_ligne1.trim() === ''
          ? null
          : {
              ligne1: adresse_ligne1.trim(),
              ligne2: ligne2 === '' ? null : ligne2,
              code_postal: adresse_code_postal.trim(),
              ville: adresse_ville.trim(),
            };
      await enregistrerAdressePrincipale(contactId, adresse);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['contact-adresse', id] });
      navigate('/clients');
    },
  });

  const onSubmit = handleSubmit((v) => {
    mutation.mutate(v);
  });
  const typePersonne = watch('type_personne');
  const aJoindre = estAJoindre(watch('kind'));
  const sansEmail = watch('sans_email');
  const etoile = aJoindre ? ' *' : '';

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/clients" aria-label="Retour">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          {isEdit ? 'Modifier le client' : 'Nouveau client'}
        </h1>
      </div>

      {mutation.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Échec : {messageErreur(mutation.error)}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Type &amp; catégorie</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="type_personne">Type</Label>
            <Select id="type_personne" {...register('type_personne')}>
              <option value="particulier">Particulier</option>
              <option value="pro">Professionnel</option>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kind">Catégorie</Label>
            <Select id="kind" {...register('kind')}>
              <option value="prospect">Prospect</option>
              <option value="client">Client</option>
              <option value="fournisseur">Fournisseur</option>
              <option value="sous_traitant">Sous-traitant</option>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="code_comptable">N° de compte client (comptabilité)</Label>
            <Input id="code_comptable" className="font-mono uppercase" {...register('code_comptable')} />
            <p className="text-xs text-muted-foreground">
              Le compte de ce client chez le comptable (C00012, par exemple). Attribué tout seul
              au premier export comptable si vous laissez vide ; une fois donné, il ne change plus.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Identité</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {typePersonne === 'pro' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="raison_sociale">Raison sociale *</Label>
                <Input id="raison_sociale" {...register('raison_sociale')} />
                {errors.nom ? <p className="text-xs text-destructive">{errors.nom.message}</p> : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="siret">SIRET</Label>
                <Input id="siret" {...register('siret')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tva_intracom">N° TVA intracom.</Label>
                <Input id="tva_intracom" {...register('tva_intracom')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="naf">Code NAF/APE</Label>
                <Input id="naf" {...register('naf')} />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="civilite">Civilité</Label>
                <Select id="civilite" {...register('civilite')}>
                  <option value="">—</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="prenom">Prénom</Label>
                <Input id="prenom" {...register('prenom')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nom">Nom *</Label>
                <Input id="nom" {...register('nom')} />
                {errors.nom ? <p className="text-xs text-destructive">{errors.nom.message}</p> : null}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Coordonnées</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="telephone_mobile">Portable{etoile}</Label>
            <Input
              id="telephone_mobile"
              type="tel"
              inputMode="tel"
              placeholder="06 12 34 56 78"
              {...register('telephone_mobile')}
            />
            {errors.telephone_mobile ? (
              <p className="text-xs text-destructive">{errors.telephone_mobile.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Relances et rappels par SMS. Déjà tapé dans « Téléphone » ? Ça suffit.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="telephone">Téléphone</Label>
            <Input id="telephone" {...register('telephone')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email{sansEmail ? '' : etoile}</Label>
            <Input id="email" type="email" disabled={sansEmail} {...register('email')} />
            {errors.email && !sansEmail ? (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            ) : null}
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" className="h-4 w-4" {...register('sans_email')} />
              Pas d&apos;e-mail : tout par courrier
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Adresse postale</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="adresse_ligne1">Numéro et rue{etoile}</Label>
            <Input
              id="adresse_ligne1"
              placeholder="12 route de Banon"
              {...register('adresse_ligne1')}
            />
            {errors.adresse_ligne1 ? (
              <p className="text-xs text-destructive">{errors.adresse_ligne1.message}</p>
            ) : null}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="adresse_ligne2">Complément</Label>
            <Input
              id="adresse_ligne2"
              placeholder="Bâtiment, lieu-dit, boîte aux lettres…"
              {...register('adresse_ligne2')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adresse_code_postal">Code postal{etoile}</Label>
            <Input
              id="adresse_code_postal"
              inputMode="numeric"
              placeholder="04300"
              {...register('adresse_code_postal')}
            />
            {errors.adresse_code_postal ? (
              <p className="text-xs text-destructive">{errors.adresse_code_postal.message}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adresse_ville">Ville{etoile}</Label>
            <Input id="adresse_ville" placeholder="Forcalquier" {...register('adresse_ville')} />
            {errors.adresse_ville ? (
              <p className="text-xs text-destructive">{errors.adresse_ville.message}</p>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground sm:col-span-2">
            C&apos;est l&apos;adresse qui s&apos;imprime sur les devis et sur les factures
            {aJoindre ? ', et celle où partent les courriers.' : ". Laissez vide si vous ne l'avez pas encore."}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Avis Google</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="avis_google">Statut de l&apos;avis</Label>
            <Select id="avis_google" {...register('avis_google')}>
              <option value="non_demande">Non demandé</option>
              <option value="demande">Demandé</option>
              <option value="laisse">Avis laissé ⭐</option>
            </Select>
            <p className="text-xs text-muted-foreground">
              Suivi de la demande d&apos;avis Google après chantier (relance par le secrétariat).
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea {...register('notes')} placeholder="Notes internes…" />
        </CardContent>
      </Card>

      {isEdit && id ? (
        <ClientSmsCard
          contactId={id}
          telephoneMobile={
            portableDuContact(watch('telephone_mobile'), watch('telephone')) ??
            watch('telephone_mobile')
          }
        />
      ) : null}

      <div className="flex gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le client'}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/clients">Annuler</Link>
        </Button>
      </div>
    </form>
  );
}
